"use client";

import { DiceControllerProvider, useDiceController } from "@rosette/web_components/src/game/dice/DiceUIContext.js";
import DiceUI from "@/components/game/dice/DiceUI.js";
import { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import React, { useEffect } from "react";
import { RoyalUrDice } from "@rosette/ts/src/business/game/royalur/RoyalUrDice.js";
import { AssetProvider, useAssetLoader } from "@/components/assets/AssetContext.js";
import { DeviceInfoProvider } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import { GameThemeProvider } from "@/components/game/theme/GameThemeContext.js";
import { GameThemeType } from "@rosette/ts/src/business/game/theme/GameThemeType.js";
import { RoyalUrRoll } from "@rosette/ts/src/business/game/royalur/RoyalUrRoll.js";
import { gameFont } from "@/components/font/gameFont.js";
import { DiceType } from "@rosette/ts/src/royalur/model/dice/DiceType.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { AudioSettings } from "@rosette/web_components/src/assets/AudioSettings.js";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";


interface InternalEmbeddedDiceProps {
    diceType: DiceType;
}

function InternalEmbeddedDice({ diceType }: InternalEmbeddedDiceProps) {

    // Enable audio.
    const assetLoader = useAssetLoader();
    useEffect(() => {
        assetLoader.getAudioSettings().set(AudioSettings.QUIET);
    }, [assetLoader]);

    // Control the dice.
    const diceController = useDiceController();
    useEffect(() => {
        const dice = new RoyalUrDice(diceType);
        diceController.pushDirectives(diceController.createWaitForRoll(
            PlayerType.LIGHT,
        ));
        return diceController.addRollListener(() => {
            const roll = RoyalUrRoll.cast(dice.roll());
            diceController.pushDirectives(diceController.createRoll(
                PlayerType.LIGHT, roll, false, false,
            ));
            diceController.pushDirectives(diceController.createWaitForRoll(
                PlayerType.LIGHT, roll,
            ));
        });
    }, [diceType, diceController]);

    return (
        <Panel className={cn(
            "relative inline-block box-content w-[18em] min-h-[10em] overflow-hidden text-base",
            "p-[0.5em] pb-[1.4em] md:p-[0.5em] md:pb-[1.4em]",
            gameFont.variable,
        )}>
            <DiceUI
                gameLayout="none"
                ignorePreferences={true}
                diceType={DiceType.FOUR_BINARY}
                leftPlayer={PlayerType.LIGHT}
                playerFilter={PlayerType.LIGHT} />

            <div className={cn(
                "absolute left-0 bottom-0 py-[0.3em] px-[0.9em] text-strong text-base font-normal",
                "bg-surface rounded-tr-2xl pointer-events-none",
            )}>
                Click to roll!
            </div>
        </Panel>
    );
}


interface EmbeddedDiceProps {
    diceTypeID: string;
    themeName: string;
}

export function EmbeddedDice({ diceTypeID, themeName }: EmbeddedDiceProps) {
    const diceType = DiceType.getByID(diceTypeID);
    const theme = GameThemeType.getByName(themeName);

    return (
        <AssetProvider>
            <DeviceInfoProvider>
                <DiceControllerProvider>
                    <GameThemeProvider themeType={theme}>
                        <InternalEmbeddedDice diceType={diceType} />
                    </GameThemeProvider>
                </DiceControllerProvider>
            </DeviceInfoProvider>
        </AssetProvider>
    );
}
